import React, { useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import useMagneticEffect from '../hooks/useMagneticEffect';

const Services = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-50px' });
  const [hoveredIndex, setHoveredIndex] = useState(null);
  const buttonRef = useMagneticEffect(0.4);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });

  const services = [
    {
      title: 'Website',
      description: 'We build impactful experiences through great UX, design, and development.',
      image: 'https://images.pexels.com/photos/6476256/pexels-photo-6476256.jpeg'
    },
    {
      title: 'Creative',
      description: 'We design visually-engaging marketing assets to help drive your brand forward.',
      image: 'https://images.pexels.com/photos/6231/marketing-color-colors-wheel.jpg'
    },
    {
      title: 'Strategy',
      description: 'We identify businesses challenges and develop solutions to achieve your goals.',
      image: 'https://images.pexels.com/photos/7661185/pexels-photo-7661185.jpeg'
    }
  ];

  return (
    <section id="services" ref={ref} className="bg-[#0f0f0f] py-16 sm:py-20 md:py-24 lg:py-32 px-4 sm:px-6 md:px-12 relative overflow-hidden">
      {/* Animated background gradient */}
      <motion.div
        style={{
          opacity: useTransform(scrollYProgress, [0, 0.5, 1], [0, 0.3, 0])
        }}
        className="absolute inset-0 bg-gradient-to-br from-orange-500/5 via-transparent to-transparent pointer-events-none"
      />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-12 sm:mb-14 md:mb-16"
        >
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-wider mb-3 sm:mb-4"
          >
            What we do
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="text-gray-400 text-base sm:text-lg font-light"
          >
            What we know and do best.
          </motion.p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 md:gap-10 lg:gap-12 mb-12 sm:mb-14 md:mb-16">
          {services.map((service, index) => (
            <motion.div
              key={service.title}
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{
                duration: 0.6,
                delay: 0.2 + index * 0.15,
                ease: [0.6, 0.05, 0.01, 0.9]
              }}
              onHoverStart={() => setHoveredIndex(index)}
              onHoverEnd={() => setHoveredIndex(null)}
              className="group cursor-pointer will-change-transform"
            >
              <div className="relative overflow-hidden rounded-lg mb-4 sm:mb-5 md:mb-6 aspect-[4/3]">
                <motion.img
                  src={service.image}
                  alt={service.title}
                  loading="lazy"
                  className="w-full h-full object-cover will-change-transform"
                  animate={{
                    scale: hoveredIndex === index ? 1.1 : 1
                  }}
                  transition={{ duration: 0.6, ease: [0.6, 0.05, 0.01, 0.9] }}
                />
                <motion.div
                  animate={{
                    opacity: hoveredIndex === index ? 0.9 : 0.7
                  }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent"
                />
                <div className="absolute bottom-4 sm:bottom-5 md:bottom-6 left-4 sm:left-5 md:left-6 right-4 sm:right-5 md:right-6">
                  <motion.h3
                    animate={{
                      color: hoveredIndex === index ? '#ff5722' : '#ffffff',
                      y: hoveredIndex === index ? -3 : 0
                    }}
                    transition={{ duration: 0.3 }}
                    className="text-2xl sm:text-3xl font-bold tracking-wider mb-2"
                  >
                    {service.title}
                  </motion.h3>
                </div>
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{
                    opacity: hoveredIndex === index ? 1 : 0,
                    scale: hoveredIndex === index ? 1 : 0.8
                  }}
                  transition={{ duration: 0.3 }}
                  className="absolute top-4 sm:top-5 md:top-6 right-4 sm:right-5 md:right-6"
                >
                  <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-orange-500 flex items-center justify-center">
                    <ArrowRight size={16} className="text-white sm:w-[18px] sm:h-[18px]" />
                  </div>
                </motion.div>
              </div>
              <motion.p
                initial={{ opacity: 0 }}
                animate={isInView ? { opacity: 1 } : {}}
                transition={{ duration: 0.5, delay: 0.4 + index * 0.15 }}
                className="text-gray-300 text-sm sm:text-base leading-relaxed"
              >
                {service.description}
              </motion.p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center"
        >
          <motion.button
            ref={buttonRef}
            whileHover={{ x: 8 }}
            className="group inline-flex items-center gap-2 sm:gap-3 text-white hover:text-orange-500 transition-colors duration-300 text-base sm:text-lg font-light tracking-wide will-change-transform"
          >
            View Our Services
            <motion.div
              animate={{ x: [0, 4, 0] }}
              transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}
            >
              <ArrowRight size={18} className="sm:w-5 sm:h-5" />
            </motion.div>
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;